import { useState } from 'react'
import { useLanguage } from '@context/LanguageContext'
import { useDocumentMetadata } from '@hooks/useDocumentMetadata'
import { cx } from '@utils/cx'
import StaticLayout from '../layout/StaticLayout'
import ProjectsGallery from '../interface/ProjectsGallery'
import ProjectModal from '../interface/modals/ProjectModal'
import CreditsModal from '../interface/modals/CreditsModal'
import { useNamespaces } from '@hooks/useNamespaces'
import { ROUTE_NAMESPACES } from '@i18n/namespaces'
import ScreenFallback from '@components/layout/ScreenFallback'
import styles from './About.module.css'

export default function About () {
  const { t } = useLanguage()
  const [creditsModalOpen, setCreditsModalOpen] = useState(false)
  const [selectedProject, setSelectedProject] = useState(null)
  const { isLoading: namespacesLoading } = useNamespaces(ROUTE_NAMESPACES.about)
  useDocumentMetadata()

  if (namespacesLoading) {
    return <ScreenFallback />
  }

  const handleProjectClick = (project) => {
    setSelectedProject(project)
  }

  return (
    <StaticLayout>
      <div className={styles.aboutPage}>
        {/* Hero Section */}
        <section className={styles.hero}>
          <h1 className={styles.heroTitle}>{t.about.hero.title}</h1>
          <p className={styles.heroDescription}>{t.about.hero.description}</p>
        </section>

        {/* Who We Are Section - Accordion */}
        <details className={styles.accordion}>
          <summary className={styles.accordionTrigger}>
            <h2 className={styles.sectionTitle}>{t.about.whoWeAre.title}</h2>
            <span className={styles.accordionIcon}>▼</span>
          </summary>
          <div className={styles.accordionContent}>
            <p className={styles.intro}>{t.about.whoWeAre.intro}</p>
            <ul className={styles.points}>
              {t.about.whoWeAre.points.map((point, index) => (
                <li key={index} className={styles.point}>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </details>

        {/* More Than a Game Section - Accordion */}
        <details className={styles.accordion}>
          <summary className={styles.accordionTrigger}>
            <h2 className={styles.sectionTitle}>{t.about.moreThanaGame.title}</h2>
            <span className={styles.accordionIcon}>▼</span>
          </summary>
          <div className={styles.accordionContent}>
            <p className={styles.description}>{t.about.moreThanaGame.description}</p>
          </div>
        </details>

        {/* Gallery Section */}
        <section className={cx(styles.section, styles.gallerySection)}>
          <h2 className={styles.sectionTitle}>{t.about.gallery.title}</h2>
          <p className={styles.description}>{t.about.gallery.description}</p>

          <ProjectsGallery onProjectClick={handleProjectClick} />
        </section>
        {selectedProject && (
          <ProjectModal
            project={selectedProject}
            onClose={() => setSelectedProject(null)}
          />
        )}

        {/* Credits Button */}
        <section className={styles.creditsSection}>
          <button
            className={styles.creditsBtn}
            onClick={() => setCreditsModalOpen(true)}
            aria-label={t.about.access.credits.button}
          >
            {t.about.credits.button}
          </button>
        </section>

        {/* Credits Modal */}
        <CreditsModal
          isOpen={creditsModalOpen}
          onClose={() => setCreditsModalOpen(false)}
        />
      </div>
    </StaticLayout>
  )
}
